import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Home, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/use-seo";
import { useLang } from "@/i18n/LanguageContext";
import type { Lang } from "@/i18n/translations";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { trackCtaClick } from "@/lib/analytics";

// Page-local i18n — kept inline like other secondary pages.
type Copy = Record<Lang, string>;
const C = (en: string, es: string, pt: string): Copy => ({ en, es, pt });

const COPY = {
  eyebrow: C("Error 404", "Error 404", "Erro 404"),
  title: C(
    "This page took the wrong shipping lane.",
    "Esta página tomó la ruta equivocada.",
    "Esta página seguiu pela rota errada.",
  ),
  body: C(
    "The page you're looking for doesn't exist or has moved. Let's get you back on track — head home, or request a quote and we'll source what you need.",
    "La página que buscas no existe o se movió. Volvamos al camino correcto — regresa al inicio o solicita una cotización y suministramos lo que necesitas.",
    "A página que você procura não existe ou foi movida. Vamos voltar ao caminho certo — volte ao início ou solicite uma cotação e fornecemos o que você precisa.",
  ),
  pathLabel: C("Requested path", "Ruta solicitada", "Caminho solicitado"),
  ctaHome: C("Back to home", "Volver al inicio", "Voltar ao início"),
  ctaQuote: C("Get Pricing & Availability", "Obtener Precio y Disponibilidad", "Obter Preço e Disponibilidade"),
  ctaWhatsApp: C("Chat on WhatsApp", "Chatear por WhatsApp", "Conversar no WhatsApp"),
  metaTitle: C(
    "Page Not Found (404) | Switzh",
    "Página No Encontrada (404) | Switzh",
    "Página Não Encontrada (404) | Switzh",
  ),
  metaDesc: C(
    "This page doesn't exist. Return home or request pricing on industrial electrical components.",
    "Esta página no existe. Vuelve al inicio o solicita precio de componentes eléctricos industriales.",
    "Esta página não existe. Volte ao início ou solicite preço de componentes elétricos industriais.",
  ),
} as const;

const NotFound = () => {
  const location = useLocation();
  const { lang, localePath } = useLang();
  const tx = (k: keyof typeof COPY) => COPY[k][lang];

  useEffect(() => {
    // Surfaces broken-link signal for ops without polluting analytics dashboards.
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  useSeo({
    title: tx("metaTitle"),
    description: tx("metaDesc"),
    lang,
  });

  // Robots: 404s should not be indexed.
  useEffect(() => {
    const prev = document.querySelector('meta[name="robots"]');
    const previousContent = prev?.getAttribute("content");
    let tag = prev as HTMLMetaElement | null;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", "noindex, follow");
    return () => {
      if (previousContent && tag) tag.setAttribute("content", previousContent);
    };
  }, []);

  const whatsAppHref = buildWhatsAppUrl(
    lang === "es"
      ? "Hola, llegué a una página inexistente en su sitio. Necesito una cotización."
      : lang === "pt"
      ? "Olá, cheguei a uma página inexistente no site. Preciso de uma cotação."
      : "Hi, I landed on a missing page on your site. I'd like a quote.",
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />

      <main className="flex flex-1 items-center">
        <div className="container px-4 py-16 md:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              {tx("eyebrow")}
            </p>

            <h1 className="mt-4 text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
              <span
                className="block bg-clip-text text-7xl font-extrabold leading-none text-transparent sm:text-8xl md:text-9xl"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary-hover)) 100%)",
                }}
              >
                404
              </span>
              <span className="mt-6 block text-2xl sm:text-3xl md:text-4xl">
                {tx("title")}
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
              {tx("body")}
            </p>

            {location.pathname && location.pathname !== "/" && (
              <p className="mt-6 inline-flex max-w-full items-center gap-2 truncate rounded-md border border-border bg-muted px-3 py-1.5 font-mono text-xs text-muted-foreground">
                <span className="text-muted-foreground/70">{tx("pathLabel")}:</span>
                <span className="truncate text-foreground">{location.pathname}</span>
              </p>
            )}

            <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <Button
                asChild
                size="lg"
                onClick={() => trackCtaClick("not_found", "404_home")}
              >
                <Link to={localePath("/")} aria-label={tx("ctaHome")}>
                  <Home className="mr-2 h-4 w-4" />
                  {tx("ctaHome")}
                </Link>
              </Button>

              <Button
                asChild
                variant="outline"
                size="lg"
                onClick={() => trackCtaClick("not_found", "404_quote")}
              >
                <Link to={localePath("/request-quote")}>
                  <ArrowLeft className="mr-2 h-4 w-4 rotate-180" />
                  {tx("ctaQuote")}
                </Link>
              </Button>

              <Button
                asChild
                variant="ghost"
                size="lg"
                onClick={() => trackCtaClick("not_found", "404_whatsapp")}
              >
                <a href={whatsAppHref} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  {tx("ctaWhatsApp")}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
